// src/components/Dashboards/ClientDashboard/ProjectTicketsView.jsx
import React, { useState, useEffect } from "react";
import ProjectService from "../../../services/ProjectService";
import TicketService from "../../../services/TicketService";
import EmployeeService from "../../../services/EmployeeService";
import { Link, useNavigate, useParams } from "react-router-dom";
import { FaEye, FaArrowLeft } from "react-icons/fa";

const ProjectTicketsView = () => {
  // Get projectId from URL parameters
  const { projectId } = useParams();
  const navigate = useNavigate();

  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState("");

  // State for create ticket dialog
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [newTicket, setNewTicket] = useState({
    title: "",
    description: "",
    priority: "MEDIUM",
    status: "OPEN",
    assignedTo: [],
  });
  const [employees, setEmployees] = useState([]);
  const [ticketFiles, setTicketFiles] = useState([]);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  // Fetch projects and employees on component mount
  useEffect(() => {
    fetchProjects();
    fetchEmployees();
  }, []);

  // Set selected project when projectId from URL changes
  useEffect(() => {
    if (projectId && projects.length > 0) {
      const project = projects.find(p => p._id === projectId);
      if (project) {
        setSelectedProject(project);
        setActiveTab(project.name);
      }
    }
  }, [projectId, projects]);

  // Fetch tickets when selected project changes
  useEffect(() => {
    if (selectedProject) {
      fetchTicketsByProject(selectedProject._id);
    }
  }, [selectedProject]);

  // Function to fetch all projects
  const fetchProjects = async () => {
    try {
      setLoading(true);
      const projectsData = await ProjectService.getAllProjects();
      setProjects(projectsData);

      // If projectId is in URL, find that project
      if (projectId) {
        const project = projectsData.find(p => p._id === projectId);
        if (project) {
          setSelectedProject(project);
          setActiveTab(project.name);
        }
      } 
      // Otherwise select the first project by default if available
      else if (projectsData.length > 0) {
        setSelectedProject(projectsData[0]);
        setActiveTab(projectsData[0].name);
      }

      setLoading(false);
    } catch (err) {
      console.error("Error fetching projects:", err);
      setError("Failed to load projects. Please try again later.");
      setLoading(false);
    }
  };

  // Function to fetch all employees
  const fetchEmployees = async () => {
    try {
      const employeesData = await EmployeeService.getAllEmployees();
      setEmployees(employeesData);
    } catch (err) {
      console.error("Error fetching employees:", err);
      setError("Failed to load employees. Please try again later.");
    }
  };

  // Function to fetch tickets by project ID
  const fetchTicketsByProject = async (projectId) => {
    try {
      setLoading(true);
      const ticketsData = await TicketService.getTicketsByProject(projectId);
      setTickets(ticketsData);
      setLoading(false);
    } catch (err) {
      console.error("Error fetching tickets:", err);
      setError("Failed to load tickets. Please try again later.");
      setLoading(false);
    }
  };

  // Handler for project selection - updates URL
  const handleSelectProject = (project) => {
    // Update URL with the selected project ID
    navigate(`/dashboard/ticket/${project._id}`);
    
    // Update state
    setSelectedProject(project);
    setActiveTab(project.name);
  };

  // Handlers for create ticket dialog
  const handleOpenCreateDialog = () => {
    setOpenCreateDialog(true);
    // Reset the form
    setNewTicket({
      title: "",
      description: "",
      priority: "MEDIUM",
      status: "OPEN",
      assignedTo: [],
    });
    setTicketFiles([]);
    setSuccessMessage("");
  };

  const handleCloseCreateDialog = () => {
    setOpenCreateDialog(false);
  };

  const handleTicketInputChange = (e) => {
    const { name, value } = e.target;
    setNewTicket({
      ...newTicket,
      [name]: value,
    });
  };

  const handleAssigneeChange = (e) => {
    setNewTicket({
      ...newTicket,
      assignedTo: Array.from(
        e.target.selectedOptions,
        (option) => option.value
      ),
    });
  };

  const handleFileChange = (e) => {
    if (e.target.files) {
      setTicketFiles(Array.from(e.target.files));
    }
  };

  const handleCreateTicket = async () => {
    if (!selectedProject) {
      setError("No project selected. Please select a project first.");
      return;
    }

    if (!newTicket.title || !newTicket.description) {
      setError("Please fill in all required fields.");
      return;
    }

    try {
      setSubmitLoading(true);

      // Add project ID to ticket data
      const ticketData = {
        ...newTicket,
        project: selectedProject._id,
      };

      // Create the ticket with files if any
      await TicketService.createTicket(
        ticketData,
        ticketFiles
      );

      // Refresh tickets list
      await fetchTicketsByProject(selectedProject._id);

      // Show success message
      setSuccessMessage("Ticket created successfully!");

      // Close dialog after a short delay
      setTimeout(() => {
        handleCloseCreateDialog();
        setSuccessMessage("");
      }, 2000);

      setSubmitLoading(false);
    } catch (err) {
      console.error("Error creating ticket:", err);
      setError("Failed to create ticket. Please try again.");
      setSubmitLoading(false);
    }
  };

  // Function to get priority color
  const getPriorityColor = (priority) => {
    switch (priority) {
      case "LOW":
        return "bg-green-100 text-green-800";
      case "MEDIUM":
        return "bg-blue-100 text-blue-800";
      case "HIGH":
        return "bg-yellow-100 text-yellow-800";
      case "URGENT":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  // Function to get status color
  const getStatusColor = (status) => {
    switch (status) {
      case "OPEN":
        return "bg-blue-100 text-blue-800";
      case "IN_PROGRESS":
        return "bg-yellow-100 text-yellow-800";
      case "PENDING":
        return "bg-purple-100 text-purple-800";
      case "RESOLVED":
        return "bg-green-100 text-green-800";
      case "CLOSED":
        return "bg-gray-100 text-gray-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  return (
    <div className="mx-auto py-8">
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      {successMessage && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
          {successMessage}
        </div>
      )}

      {/* Project Selection Tabs */}
      <div className="bg-white shadow rounded-lg p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold">Project Tickets</h2>
        </div>

        {loading && !projects.length ? (
          <div className="flex justify-center my-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          </div>
        ) : (
          <div className="border-b border-gray-200 mb-6">
            <nav className="-mb-px flex space-x-8 overflow-x-auto">
              {projects.map((project) => (
                <button
                  key={project._id}
                  onClick={() => handleSelectProject(project)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                    activeTab === project.name
                      ? "border-blue-500 text-blue-600"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }`}
                >
                  {project.name}
                </button>
              ))}
            </nav>
          </div>
        )}
        
        {/* Project Content Based on Active Tab */}
        <>
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold">
              {selectedProject
                ? `Tickets for ${selectedProject.name}`
                : "Select a project to view tickets"}
            </h2>

            <button
              className={`flex items-center px-4 py-2 rounded-md transition-colors ${
                !selectedProject
                  ? "bg-gray-300 cursor-not-allowed"
                  : "bg-blue-600 hover:bg-blue-700 text-white"
              }`}
              onClick={handleOpenCreateDialog}
              disabled={!selectedProject}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5 mr-1"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z"
                  clipRule="evenodd"
                />
              </svg>
              Create Ticket
            </button>
          </div>

          <div className="bg-white shadow rounded-lg overflow-hidden">
            {loading && selectedProject ? (
              <div className="flex justify-center p-8">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Ticket ID
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Title
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Priority
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Created
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {tickets.length > 0 ? (
                      tickets.map((ticket) => (
                        <tr 
                        key={ticket._id} 
                        className="hover:bg-gray-50"
                        onClick={() => navigate(`/dashboard/tickets/${ticket._id}`)}
                        >
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            <Link to={`/dashboard/tickets/${ticket._id}`}>
                              {ticket.kkgtTicketId}
                            </Link>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {ticket.title}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            <span
                              className={`px-2 py-1 text-xs rounded-full ${getPriorityColor(
                                ticket.priority
                              )}`}
                            >
                              {ticket.priority}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            <span
                              className={`px-2 py-1 text-xs rounded-full ${getStatusColor(
                                ticket.status
                              )}`}
                            >
                              {ticket.status.replace("_", " ")}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {new Date(ticket.createdAt).toLocaleDateString(
                              "en-US",
                              {
                                year: "numeric",
                                month: "short",
                                day: "numeric",
                              }
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            <Link to={`/dashboard/tickets/${ticket._id}`}>
                              <FaEye className="text-lg" />
                            </Link>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td
                          colSpan="6"
                          className="px-6 py-4 text-center text-sm text-gray-500"
                        >
                          {selectedProject
                            ? "No tickets found for this project"
                            : "Select a project to view tickets"}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      </div>

      {/* Create Ticket Dialog */}
      {openCreateDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-screen overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold">
                Create New Ticket for {selectedProject?.name}
              </h3>
            </div>

            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Ticket Title *
                  </label>
                  <input
                    type="text"
                    name="title"
                    value={newTicket.title}
                    onChange={handleTicketInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description *
                  </label>
                  <textarea
                    name="description"
                    rows="4"
                    value={newTicket.description}
                    onChange={handleTicketInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  ></textarea>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Priority
                  </label>
                  <select
                    name="priority"
                    value={newTicket.priority}
                    onChange={handleTicketInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="LOW">Low</option>
                    <option value="MEDIUM">Medium</option>
                    <option value="HIGH">High</option>
                    <option value="URGENT">Urgent</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Status
                  </label>
                  <select
                    name="status"
                    value={newTicket.status}
                    onChange={handleTicketInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="OPEN">Open</option>
                    {/* <option value="IN_PROGRESS">In Progress</option>
                    <option value="PENDING">Pending</option>
                    <option value="RESOLVED">Resolved</option>
                    <option value="CLOSED">Closed</option> */}
                  </select>
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Attachments
                  </label>
                  <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md">
                    <div className="space-y-1 text-center">
                      <svg
                        className="mx-auto h-12 w-12 text-gray-400"
                        stroke="currentColor"
                        fill="none"
                        viewBox="0 0 48 48"
                      >
                        <path
                          d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                      <div className="flex text-sm text-gray-600">
                        <label
                          htmlFor="file-upload"
                          className="relative cursor-pointer bg-white rounded-md font-medium text-blue-600 hover:text-blue-500 focus-within:outline-none"
                        >
                          <span>Upload files</span>
                          <input
                            id="file-upload"
                            name="file-upload"
                            type="file"
                            className="sr-only"
                            multiple
                            onChange={handleFileChange}
                          />
                        </label>
                        <p className="pl-1">or drag and drop</p>
                      </div>
                      <p className="text-xs text-gray-500">
                        PNG, JPG, PDF up to 10MB
                      </p>
                    </div>
                  </div>

                  {ticketFiles.length > 0 && (
                    <div className="mt-3">
                      <h4 className="text-sm font-medium text-gray-700">
                        Selected Files:
                      </h4>
                      <div className="mt-2 flex flex-wrap gap-2">
                        {ticketFiles.map((file, index) => (
                          <span
                            key={index}
                            className="inline-flex items-center bg-gray-100 text-gray-800 text-xs px-2 py-1 rounded-full"
                          >
                            {file.name}
                            <button
                              type="button"
                              className="ml-1 text-gray-500 hover:text-gray-700"
                              onClick={(e) => {
                                e.stopPropagation(); // Prevent row click
                                const newFiles = [...ticketFiles];
                                newFiles.splice(index, 1);
                                setTicketFiles(newFiles);
                              }}
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="h-4 w-4"
                                viewBox="0 0 20 20"
                                fill="currentColor"
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                                  clipRule="evenodd"
                                />
                              </svg>
                            </button>
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-2">
              <button
                type="button"
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                onClick={handleCloseCreateDialog}
              >
                Cancel
              </button>
              <button
                type="button"
                className={`px-4 py-2 text-sm font-medium text-white rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  submitLoading || !newTicket.title || !newTicket.description
                    ? "bg-blue-400 cursor-not-allowed"
                    : "bg-blue-600 hover:bg-blue-700"
                }`}
                onClick={handleCreateTicket}
                disabled={
                  submitLoading || !newTicket.title || !newTicket.description
                }
              >
                {submitLoading ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Processing...
                  </div>
                ) : (
                  "Create Ticket"
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectTicketsView;